import { useCallback, useEffect, useRef, useState } from 'react';
import { chatApi } from '@/api';
import { useCancelToken } from '@/hooks/useCancelToken';
import {
  MAX_MESSAGES_IN_STATE,
  LATEST_MESSAGES_UP_COUNT,
  MESSAGE_CONTEXT_DEFAULT_UP,
  MESSAGE_CONTEXT_DEFAULT_DOWN,
  MESSAGE_CONTEXT_UP_FOR_OLDER,
  MESSAGE_CONTEXT_DOWN_FOR_OLDER,
  MESSAGE_CONTEXT_UP_FOR_NEWER,
  MESSAGE_CONTEXT_DOWN_FOR_NEWER,
  MESSAGE_SCROLL_TOP_THRESHOLD,
  MESSAGE_SCROLL_BOTTOM_THRESHOLD,
} from '../constants';
import {
  appendUniqueMessages,
  appendUploadedFileToMessage,
  getOldestMessageId,
  getNewestMessageId,
  hasMessageById,
  normalizeChatMessages,
  prependUniqueMessages,
  removeMessageById,
  replaceMessageById,
  trimMessagesWindow,
} from '../utils/messages';
import {
  captureScrollSnapshot,
  restoreScrollAfterPrepend,
} from '../utils/scroll';

export const useChatMessages = ({
  chatId,
  topicId = null,
  containerRef,
  normalizeMessage,
}) => {
  const {
    getCancelTokenSource: getMessageContextCancelTokenSource,
    cancel: cancelMessageContextRequest,
  } = useCancelToken();
  const [dataMessages, setDataMessages] = useState([]);
  const [messagesViewMode, setMessagesViewMode] = useState('latest');
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
  const [isLoadingNewerMessages, setIsLoadingNewerMessages] = useState(false);
  const [isLoadingMessageContext, setIsLoadingMessageContext] = useState(false);
  const [hasOlderMessages, setHasOlderMessages] = useState(true);
  const [hasNewerMessages, setHasNewerMessages] = useState(false);
  const [pendingScrollTargetId, setPendingScrollTargetId] = useState(null);
  const [isOlderMessagesScrollEnabled, setIsOlderMessagesScrollEnabled] = useState(false);

  const dataMessagesRef = useRef([]);
  const previousScrollTopRef = useRef(null);
  const isLoadingOlderMessagesRef = useRef(false);
  const isLoadingNewerMessagesRef = useRef(false);
  const isLoadingMessageContextRef = useRef(false);
  const latestMessagesRequestIdRef = useRef(0);
  const messageContextRequestIdRef = useRef(0);
  const currentChatIdRef = useRef(chatId);
  const currentTopicIdRef = useRef(topicId);
  const isMountedRef = useRef(true);

  useEffect(() => {
    currentChatIdRef.current = chatId;
    currentTopicIdRef.current = topicId;
    dataMessagesRef.current = [];
    latestMessagesRequestIdRef.current += 1;
    messageContextRequestIdRef.current += 1;
    cancelMessageContextRequest('Смена чата или темы');
    previousScrollTopRef.current = null;
    isLoadingOlderMessagesRef.current = false;
    isLoadingNewerMessagesRef.current = false;
    isLoadingMessageContextRef.current = false;
    setDataMessages([]);
    setMessagesViewMode('latest');
    setIsLoadingOlderMessages(false);
    setIsLoadingNewerMessages(false);
    setIsLoadingMessageContext(false);
    setHasOlderMessages(true);
    setHasNewerMessages(false);
    setPendingScrollTargetId(null);
    setIsOlderMessagesScrollEnabled(false);
  }, [chatId, topicId]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      latestMessagesRequestIdRef.current += 1;
      messageContextRequestIdRef.current += 1;
      cancelMessageContextRequest('Компонент размонтирован');
      isLoadingOlderMessagesRef.current = false;
      isLoadingNewerMessagesRef.current = false;
      isLoadingMessageContextRef.current = false;
    };
  }, []);

  const getContextParams = useCallback((params = {}, nextTopicId = topicId) => ({
    ...params,
    chat_id: chatId,
    ...(nextTopicId === null || nextTopicId === undefined
      ? {}
      : { topic_id: nextTopicId }),
  }), [chatId, topicId]);

  const getPreparedContextData = useCallback((response) => {
    return {
      messages: normalizeChatMessages(
        response?.data?.messages ?? [],
        normalizeMessage,
      ),
      hasUp: Boolean(response?.data?.has_up),
      hasDown: Boolean(response?.data?.has_down),
      pivotMessageId:
        response?.data?.pivot_message_id ??
        response?.data?.pivotMessageId ??
        null,
    };
  }, [normalizeMessage]);

  const updateMessages = useCallback((updater) => {
    setDataMessages((prevMessages) => {
      const nextMessages =
        typeof updater === 'function' ? updater(prevMessages) : updater;

      dataMessagesRef.current = nextMessages;
      return nextMessages;
    });
  }, []);

  const setInitialMessages = useCallback((messages = [], options = {}) => {
    const normalizedMessages = normalizeChatMessages(messages, normalizeMessage);
    const nextMessages = trimMessagesWindow(normalizedMessages, {
      maxCount: MAX_MESSAGES_IN_STATE,
      mode: 'latest',
    });

    dataMessagesRef.current = nextMessages;
    setDataMessages(nextMessages);
    setMessagesViewMode('latest');
    setHasOlderMessages(Boolean(options.hasUp ?? nextMessages.length > 0));
    setHasNewerMessages(Boolean(options.hasDown));
  }, [normalizeMessage]);

  const appendIncomingMessage = useCallback((message) => {
    updateMessages((prevMessages) =>
      trimMessagesWindow(
        appendUniqueMessages(prevMessages, [normalizeMessage(message)]),
        { maxCount: MAX_MESSAGES_IN_STATE, mode: 'latest' },
      ),
    );
  }, [normalizeMessage, updateMessages]);

  const appendUploadedFile = useCallback((messageId, file) => {
    updateMessages((prevMessages) =>
      appendUploadedFileToMessage(prevMessages, messageId, file),
    );
  }, [updateMessages]);

  const appendTempMessage = useCallback((message) => {
    const tempMessageId = `new_${Date.now()}`;

    updateMessages((prevMessages) => [
      ...prevMessages,
      {
        ...message,
        id: tempMessageId,
      },
    ]);

    return tempMessageId;
  }, [updateMessages]);

  const replaceTempMessage = useCallback((tempMessageId, message) => {
    updateMessages((prevMessages) =>
      replaceMessageById(prevMessages, tempMessageId, normalizeMessage(message)),
    );
  }, [normalizeMessage, updateMessages]);

  const removeMessage = useCallback((messageId) => {
    updateMessages((prevMessages) => removeMessageById(prevMessages, messageId));
  }, [updateMessages]);

  const updateMessageReactions = useCallback((messageId, reactions = []) => {
    if (!messageId) return;

    updateMessages((prevMessages) => prevMessages.map((message) => (
      String(message?.id) === String(messageId)
        ? {
          ...message,
          reactions: Array.isArray(reactions) ? reactions : [],
        }
        : message
    )));
  }, [updateMessages]);

  const loadOlderMessages = useCallback(async () => {
    if (isLoadingOlderMessagesRef.current || !hasOlderMessages) return;

    const oldestMessageId = getOldestMessageId(dataMessagesRef.current);
    if (!oldestMessageId) return;

    isLoadingOlderMessagesRef.current = true;
    setIsLoadingOlderMessages(true);
    const scrollSnapshot = captureScrollSnapshot(containerRef.current);
    const cancelTokenSource = getMessageContextCancelTokenSource();

    try {
      const response = await chatApi.messageContext(getContextParams({
        id: oldestMessageId,
        up: MESSAGE_CONTEXT_UP_FOR_OLDER,
        down: MESSAGE_CONTEXT_DOWN_FOR_OLDER,
      }), cancelTokenSource);

      if (
        !isMountedRef.current ||
        currentChatIdRef.current !== chatId ||
        String(currentTopicIdRef.current) !== String(topicId)
      ) return;

      const {
        messages: normalizedMessages,
        hasUp,
        hasDown,
      } = getPreparedContextData(response);

      updateMessages((prevMessages) => {
        const nextMessages = prependUniqueMessages(prevMessages, normalizedMessages);

        return trimMessagesWindow(nextMessages, {
          maxCount: MAX_MESSAGES_IN_STATE,
          mode: 'prepend',
        });
      });

      setHasOlderMessages(hasUp);
      setHasNewerMessages(hasDown);

      requestAnimationFrame(() => {
        restoreScrollAfterPrepend(containerRef.current, scrollSnapshot);
      });
    } finally {
      isLoadingOlderMessagesRef.current = false;
      if (isMountedRef.current) {
        setIsLoadingOlderMessages(false);
      }
    }
  }, [
    chatId,
    containerRef,
    getContextParams,
    getPreparedContextData,
    hasOlderMessages,
    topicId,
    updateMessages,
  ]);

  const loadNewerMessages = useCallback(async () => {
    if (isLoadingNewerMessagesRef.current || !hasNewerMessages) return;

    const newestMessageId = getNewestMessageId(dataMessagesRef.current);
    if (!newestMessageId) return;

    isLoadingNewerMessagesRef.current = true;
    setIsLoadingNewerMessages(true);
    const cancelTokenSource = getMessageContextCancelTokenSource();

    try {
      const response = await chatApi.messageContext(getContextParams({
        id: newestMessageId,
        up: MESSAGE_CONTEXT_UP_FOR_NEWER,
        down: MESSAGE_CONTEXT_DOWN_FOR_NEWER,
      }), cancelTokenSource);

      if (
        !isMountedRef.current ||
        currentChatIdRef.current !== chatId ||
        String(currentTopicIdRef.current) !== String(topicId)
      ) return;

      const {
        messages: normalizedMessages,
        hasUp,
        hasDown,
      } = getPreparedContextData(response);

      updateMessages((prevMessages) =>
        trimMessagesWindow(
          appendUniqueMessages(prevMessages, normalizedMessages),
          {
            maxCount: MAX_MESSAGES_IN_STATE,
            mode: 'append',
          },
        ),
      );

      setHasOlderMessages(hasUp);
      setHasNewerMessages(hasDown);
    } finally {
      isLoadingNewerMessagesRef.current = false;
      if (isMountedRef.current) {
        setIsLoadingNewerMessages(false);
      }
    }
  }, [
    chatId,
    getContextParams,
    getPreparedContextData,
    hasNewerMessages,
    topicId,
    updateMessages,
  ]);

  const refreshLatestMessages = useCallback(async () => {
    const requestId = latestMessagesRequestIdRef.current + 1;
    latestMessagesRequestIdRef.current = requestId;

    const response = await chatApi.messageContext(getContextParams({
      up: LATEST_MESSAGES_UP_COUNT,
    }));

    if (
      !isMountedRef.current ||
      currentChatIdRef.current !== chatId ||
      String(currentTopicIdRef.current) !== String(topicId) ||
      latestMessagesRequestIdRef.current !== requestId
    ) return;

    const {
      messages: normalizedMessages,
      hasUp,
      hasDown,
    } = getPreparedContextData(response);

    updateMessages((prevMessages) => {
      const incomingById = new Map(normalizedMessages.map((message) => [String(message?.id), message]));
      const existingIds = new Set(prevMessages.map((message) => String(message?.id)));
      const mergedMessages = prevMessages.map((message) =>
        incomingById.get(String(message?.id)) || message,
      );
      const newMessages = normalizedMessages.filter((message) => !existingIds.has(String(message?.id)));

      return trimMessagesWindow(
        [...mergedMessages, ...newMessages],
        {
          maxCount: MAX_MESSAGES_IN_STATE,
          mode: 'latest',
        },
      );
    });

    setHasOlderMessages(hasUp);
    setHasNewerMessages(hasDown);
  }, [
    chatId,
    getContextParams,
    getPreparedContextData,
    topicId,
    updateMessages,
  ]);

  const openMessageContext = useCallback(async (messageId, nextTopicId = topicId) => {
    if (!messageId) return;

    if (
      String(nextTopicId) === String(currentTopicIdRef.current) &&
      hasMessageById(dataMessagesRef.current, messageId)
    ) {
      setPendingScrollTargetId(messageId);
      return;
    }

    if (isLoadingMessageContextRef.current) return;

    const requestId = messageContextRequestIdRef.current + 1;
    messageContextRequestIdRef.current = requestId;
    isLoadingMessageContextRef.current = true;
    setIsLoadingMessageContext(true);
    const cancelTokenSource = getMessageContextCancelTokenSource();

    try {
      const response = await chatApi.messageContext(getContextParams({
        id: messageId,
        up: MESSAGE_CONTEXT_DEFAULT_UP,
        down: MESSAGE_CONTEXT_DEFAULT_DOWN,
      }, nextTopicId), cancelTokenSource);

      if (
        !isMountedRef.current ||
        currentChatIdRef.current !== chatId ||
        String(currentTopicIdRef.current) !== String(nextTopicId)
      ) return;

      const {
        messages: normalizedMessages,
        hasUp,
        hasDown,
        pivotMessageId,
      } = getPreparedContextData(response);

      if (messageContextRequestIdRef.current !== requestId) return;

      const nextMessages = trimMessagesWindow(normalizedMessages, {
        maxCount: MAX_MESSAGES_IN_STATE,
        mode: 'latest',
      });

      dataMessagesRef.current = nextMessages;
      setDataMessages(nextMessages);
      setMessagesViewMode('context');
      setHasOlderMessages(hasUp);
      setHasNewerMessages(hasDown);
      setPendingScrollTargetId(pivotMessageId ?? messageId);
    } finally {
      if (isMountedRef.current && messageContextRequestIdRef.current === requestId) {
        isLoadingMessageContextRef.current = false;
        setIsLoadingMessageContext(false);
      }
    }
  }, [
    chatId,
    getContextParams,
    getPreparedContextData,
    topicId,
  ]);

  const returnToLatestMessages = useCallback(async (nextTopicId = topicId) => {
    const requestId = latestMessagesRequestIdRef.current + 1;
    latestMessagesRequestIdRef.current = requestId;
    const cancelTokenSource = getMessageContextCancelTokenSource();

    const response = await chatApi.messageContext(getContextParams({
      up: LATEST_MESSAGES_UP_COUNT,
    }, nextTopicId), cancelTokenSource);

    if (
      !isMountedRef.current ||
      currentChatIdRef.current !== chatId ||
      String(currentTopicIdRef.current) !== String(nextTopicId)
    ) return;

    const {
      messages: normalizedMessages,
      hasUp,
      hasDown,
    } = getPreparedContextData(response);

    if (latestMessagesRequestIdRef.current !== requestId) return;

    setInitialMessages(normalizedMessages, { hasUp, hasDown });
    setPendingScrollTargetId(null);
  }, [
    chatId,
    getContextParams,
    getPreparedContextData,
    setInitialMessages,
    topicId,
  ]);

  const handleMessagesScroll = useCallback((event) => {
    if (!isOlderMessagesScrollEnabled) return;
    if (isLoadingMessageContext) return;
    const {
      scrollTop: currentScrollTop,
      clientHeight,
      scrollHeight,
    } = event.currentTarget;
    const previousScrollTop =
      previousScrollTopRef.current === null
        ? currentScrollTop
        : previousScrollTopRef.current;

    previousScrollTopRef.current = currentScrollTop;

    const bottomOffset = scrollHeight - (currentScrollTop + clientHeight);

    if (bottomOffset <= MESSAGE_SCROLL_BOTTOM_THRESHOLD) {
      loadNewerMessages();
    }

    if (currentScrollTop < previousScrollTop) {
      if (currentScrollTop <= MESSAGE_SCROLL_TOP_THRESHOLD) {
        loadOlderMessages();
      }
      return;
    }
  }, [
    isLoadingMessageContext,
    isOlderMessagesScrollEnabled,
    loadNewerMessages,
    loadOlderMessages,
  ]);

  const clearPendingScrollTarget = useCallback(() => {
    setPendingScrollTargetId(null);
  }, []);

  const enableOlderMessagesScroll = useCallback(() => {
    previousScrollTopRef.current = containerRef.current?.scrollTop ?? null;
    setIsOlderMessagesScrollEnabled(true);
  }, [containerRef]);

  const disableOlderMessagesScroll = useCallback(() => {
    previousScrollTopRef.current = null;
    setIsOlderMessagesScrollEnabled(false);
  }, []);

  return {
    dataMessages,
    messagesViewMode,
    isLoadingOlderMessages,
    isLoadingNewerMessages,
    isLoadingMessageContext,
    hasOlderMessages,
    hasNewerMessages,
    pendingScrollTargetId,
    isOlderMessagesScrollEnabled,
    setInitialMessages,
    appendIncomingMessage,
    appendUploadedFile,
    appendTempMessage,
    replaceTempMessage,
    removeMessage,
    updateMessageReactions,
    loadOlderMessages,
    loadNewerMessages,
    refreshLatestMessages,
    openMessageContext,
    returnToLatestMessages,
    handleMessagesScroll,
    clearPendingScrollTarget,
    enableOlderMessagesScroll,
    disableOlderMessagesScroll,
  };
};
